/**
 * ReportShell — the Reporting section's shell (task 035-01, README decision 1).
 *
 * One section, four tabs, one nav item: `sidebar.tsx` keeps its single
 * `Reporting` entry pointed at `/reports/co-op`; this shell renders the
 * section's `PageHeader` plus a tab strip over `/reports/co-op`,
 * `/reports/custom-optin`, `/reports/cto`, `/reports/paid-social`. Each 035
 * screen wraps its body in `<ReportShell>`; `App.tsx` owns the routes
 * (declared merge point) and the `/reports` → `/reports/co-op` redirect.
 *
 * `REPORT_TABS` is the single source of the tab set — task 11's Paid-Social
 * slip gate removes a tab by deleting its one entry here, and
 * `report-shell.test.tsx` asserts the tab strip from this array rather than a
 * literal four, so that deletion needs no test edit.
 *
 * Task 087-01: every tab link carries the shared selection keys (`range`,
 * `from`, `to`, `status`, `test`) of the current location, and no others.
 */

import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { PageHeader } from '../../components/ui/page-header';
import { carrySharedReportParams } from './report-filters';

export interface ReportTab {
  /** Path segment under `/reports/` — `co-op`, `custom-optin`, `cto`, `paid-social`. */
  path: string;
  label: string;
}

/** Order matches the README's column-set order (Co-op, Custom Opt-in, CTO Email, Paid Social); task 069-01 adds the union tab beside the two it unites; task 073-02 adds the between-traffics view beside its CTO Email sibling. */
export const REPORT_TABS: ReportTab[] = [
  { path: 'co-op', label: 'Co-op' },
  { path: 'custom-optin', label: 'Custom Opt-in' },
  { path: 'cto', label: 'CTO Email' },
  { path: 'cto-common', label: 'CTO Common' },
  { path: 'paid-social', label: 'Paid Social' },
  { path: 'cto-paid-social', label: 'CTO + Paid Social' },
  { path: 'daily-spend', label: 'Daily Spend' },
];

interface ReportShellProps {
  title: string;
  desc?: string;
  actions?: ReactNode;
  children: ReactNode;
}

/** Same active/inactive treatment as the preset strip's buttons (report-filters.tsx). */
const tabClassName = (isActive: boolean) =>
  `rounded-md px-2 py-0.5 text-xs font-medium transition ${
    isActive ? 'bg-accent text-white' : 'bg-paper text-faint hover:text-ink hairline'
  }`;

export function ReportShell({ title, desc, actions, children }: ReportShellProps) {
  const location = useLocation();
  // The date, status and test-leads selection follow her from tab to tab (087-01 D2).
  const search = carrySharedReportParams(location.search);

  return (
    <div>
      <PageHeader kicker="Reports" title={title} desc={desc} actions={actions} />

      <nav aria-label="Reports" className="mb-4 flex gap-1">
        {REPORT_TABS.map((tab) => (
          <NavLink
            key={tab.path}
            to={{ pathname: `/reports/${tab.path}`, search }}
            className={({ isActive }) => tabClassName(isActive)}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {children}
    </div>
  );
}
